/**
 * Floating support button — the customer's way into the OM Asiakasdeski.
 *
 * One button, one dialog, one message. Everything after this is the desk's job:
 * the customer gets an automatic acknowledgement, Kalle approves the real
 * answer in the Deski app. Nothing here promises a timeline.
 *
 * Two rules shape the UI:
 * - the exercise moment stays bare (see useSupportWidgetStore)
 * - the customer sees exactly what is attached before sending, and it is only
 *   browser, view and recent errors — never anything they said
 *
 * Hosts get the guide panel instead (#1046 chat, grown into a side panel in
 * #1165, components/guide/HostGuidePanel.tsx); "Send as feedback instead" brings
 * them back to this form.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { useTranslation } from '../hooks/useTranslation';
import { useBottomOverlayInset } from '../hooks/useBottomOverlayInset';
import { collectAttachments, sendSupportFeedback, type SupportSurface } from '../lib/supportApi';
import { isParticipantPath, resolveSupportWidgetMode } from '../lib/supportWidgetMode';
import { useAuthStore } from '../store/useAuthStore';
import { useSupportWidgetStore } from '../store/useSupportWidgetStore';
import { color, cx, typo } from '../styles/chalkboardTokens';
import { HostGuidePanel } from './guide/HostGuidePanel';

const MAX_IMAGES = 5;
const MAX_IMAGE_BYTES = 3 * 1024 * 1024;

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type Status = 'idle' | 'sending' | 'sent' | 'error';

const readImage = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result || ''));
        reader.onerror = () => reject(new Error('read failed'));
        reader.readAsDataURL(file);
    });

const SupportFeedbackWidget = () => {
    const { t } = useTranslation();
    const location = useLocation();
    const hiddenBy = useSupportWidgetStore((state) => state.hiddenBy);
    const token = useAuthStore((state) => state.token);
    const user = useAuthStore((state) => state.user);

    const [open, setOpen] = useState(false);
    const [message, setMessage] = useState('');
    const [email, setEmail] = useState('');
    const [images, setImages] = useState<string[]>([]);
    const [showDetails, setShowDetails] = useState(false);
    const [status, setStatus] = useState<Status>('idle');
    const [error, setError] = useState<string | null>(null);
    const bottomInset = useBottomOverlayInset();
    const fileInputRef = useRef<HTMLInputElement>(null);

    // A host who chose "Send as feedback instead" from the guide panel.
    const [forceFeedback, setForceFeedback] = useState(false);

    const attachments = useMemo(
        () => (open ? collectAttachments() : { device: '', context: '' }),
        // Re-read on every open so the view/error list matches what the user sees.
        [open, location.pathname],
    );

    const onParticipantSurface = isParticipantPath(location.pathname);
    const surface: SupportSurface = onParticipantSurface ? 'participant' : 'public';

    // Signed in, mid-journey or just reading the site — everyone gets a way to
    // reach a person. Only the exercise moment is left alone.
    const visible = hiddenBy === 0;

    // Hosts get the guide panel; everyone else (public, participants — always)
    // gets the form. "Send as feedback instead" flips a host back to the form.
    const mode = resolveSupportWidgetMode({
        authenticated: Boolean(token),
        role: user?.role ?? null,
        pathname: location.pathname,
    });
    const showGuide = mode === 'hostChat' && !forceFeedback && Boolean(user?.id);

    // A visitor on the public site is not "giving feedback", they want to reach
    // us. Same pipe, different words.
    const isOutreach = !token && surface === 'public';
    const emailValid = email.trim().length === 0 || EMAIL_PATTERN.test(email.trim());

    const addImages = useCallback(async (files: FileList | File[]) => {
        const incoming: string[] = [];
        for (const file of Array.from(files)) {
            if (!file.type.startsWith('image/')) {
                continue;
            }
            try {
                incoming.push(await readImage(file));
            } catch {
                // A screenshot that will not read must not block the message
            }
        }
        setImages((current) => {
            const next = [...current, ...incoming].slice(0, MAX_IMAGES);
            let total = 0;
            return next.filter((image) => {
                total += Math.floor((image.length * 3) / 4);
                return total <= MAX_IMAGE_BYTES;
            });
        });
    }, []);

    // Desktop convenience: paste a screenshot straight into the dialog.
    useEffect(() => {
        if (!open) {
            return;
        }
        const onPaste = (event: ClipboardEvent) => {
            const files = Array.from(event.clipboardData?.files || []);
            if (files.length > 0) {
                void addImages(files);
            }
        };
        const onKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                setOpen(false);
            }
        };
        window.addEventListener('paste', onPaste);
        window.addEventListener('keydown', onKey);
        return () => {
            window.removeEventListener('paste', onPaste);
            window.removeEventListener('keydown', onKey);
        };
    }, [open, addImages]);

    const close = useCallback(() => {
        setOpen(false);
        setStatus('idle');
        setError(null);
        setMessage('');
        setEmail('');
        setImages([]);
        setShowDetails(false);
        // Only the mode override resets; the guide keeps its own conversation.
        setForceFeedback(false);
    }, []);

    const submit = useCallback(async () => {
        if (message.trim().length < 3 || !emailValid) {
            return;
        }
        setStatus('sending');
        setError(null);
        try {
            await sendSupportFeedback({
                message: message.trim(),
                // No subject: the desk uses the start of the message, and a
                // generated one just repeated the text in the register.
                images,
                device: attachments.device,
                context: attachments.context,
                email: token ? undefined : email.trim() || undefined,
                surface,
            });
            setStatus('sent');
            setTimeout(close, 2600);
        } catch (submitError) {
            const detail =
                typeof submitError === 'object' && submitError !== null && 'response' in submitError
                    ? (submitError as { response?: { data?: { message?: string } } }).response?.data?.message
                    : undefined;
            setStatus('error');
            setError(detail || t('support.error'));
        }
    }, [message, email, emailValid, images, attachments, surface, token, close, t]);

    if (!visible) {
        return null;
    }

    if (showGuide) {
        return (
            <HostGuidePanel
                userId={user!.id}
                visible={visible}
                onSendAsFeedback={() => {
                    setForceFeedback(true);
                    setOpen(true);
                }}
            />
        );
    }

    return (
        <>
            {!open && (
                <button
                    type="button"
                    onClick={() => setOpen(true)}
                    style={{ bottom: bottomInset }}
                    className={cx(
                        'fixed right-4 z-40 rounded-md border px-3 py-2 text-sm shadow-lg backdrop-blur transition',
                        color.auto.borderSubtle,
                        color.auto.surface,
                        color.auto.textMuted,
                        'hover:shadow-emerald-500/10',
                    )}
                    aria-label={isOutreach ? t('support.buttonPublic') : t('support.button')}
                >
                    {isOutreach ? t('support.buttonPublic') : t('support.button')}
                </button>
            )}

            {/* #1060: top-anchored on phones (items-start) with a scrollable backdrop, so the
                text field stays visible above the software keyboard, which covers the bottom
                half of the screen exactly where an items-end sheet used to sit. */}
            {open && (
                <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-slate-950/50 p-4 sm:items-center">
                    <div
                        role="dialog"
                        aria-modal="true"
                        aria-label={isOutreach ? t('support.titlePublic') : t('support.title')}
                        className={cx(
                            'my-2 max-h-[92svh] w-full max-w-lg overflow-y-auto rounded-2xl border p-6 shadow-xl',
                            color.auto.borderSubtle,
                            color.auto.bg,
                        )}
                    >
                        <h2 className={typo.auto.h3}>
                            {isOutreach ? t('support.titlePublic') : t('support.title')}
                        </h2>
                        <p className={cx('mt-2', typo.auto.bodyMuted)}>
                            {token
                                ? t('support.introHost')
                                : isOutreach
                                    ? t('support.introPublic')
                                    : t('support.introParticipant')}
                        </p>

                        {status === 'sent' ? (
                            <p className={cx('mt-6 text-sm', color.auto.success)}>
                                {token || email.trim() ? t('support.sentWithReply') : t('support.sent')}
                            </p>
                        ) : (
                            <>
                                <textarea
                                    value={message}
                                    onChange={(event) => setMessage(event.target.value)}
                                    rows={5}
                                    maxLength={4000}
                                    autoFocus
                                    placeholder={isOutreach ? t('support.placeholderPublic') : t('support.placeholder')}
                                    className={cx(
                                        'mt-4 w-full rounded-md border p-3 text-base outline-none sm:text-sm',
                                        color.auto.borderSubtle,
                                        color.auto.surface,
                                        color.auto.textPrimary,
                                    )}
                                />

                                {!token && (
                                    <div className="mt-3">
                                        <label className={cx('block text-xs', color.auto.textMuted)} htmlFor="support-email">
                                            {t('support.emailLabel')}
                                        </label>
                                        <input
                                            id="support-email"
                                            type="email"
                                            inputMode="email"
                                            autoComplete="email"
                                            value={email}
                                            onChange={(event) => setEmail(event.target.value)}
                                            maxLength={200}
                                            placeholder={t('support.emailPlaceholder')}
                                            className={cx(
                                                'mt-1 w-full rounded-md border p-2 text-base outline-none sm:text-sm',
                                                color.auto.borderSubtle,
                                                color.auto.surface,
                                                color.auto.textPrimary,
                                            )}
                                        />
                                        <p className={cx('mt-1 text-[11px]', color.auto.textFaint)}>
                                            {t('support.emailConsent')}
                                        </p>
                                        {!emailValid && (
                                            <p className={cx('mt-1 text-xs', color.auto.error)}>{t('support.emailInvalid')}</p>
                                        )}
                                    </div>
                                )}

                                <div className="mt-3 flex flex-wrap items-center gap-2">
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        accept="image/*"
                                        multiple
                                        className="hidden"
                                        onChange={(event) => {
                                            if (event.target.files) {
                                                void addImages(event.target.files);
                                            }
                                            event.target.value = '';
                                        }}
                                    />
                                    <button
                                        type="button"
                                        onClick={() => fileInputRef.current?.click()}
                                        className={cx(
                                            'rounded-md border px-3 py-1.5 text-xs',
                                            color.auto.borderSubtle,
                                            color.auto.textMuted,
                                        )}
                                    >
                                        {t('support.attach')}
                                    </button>
                                    {images.map((image, index) => (
                                        <button
                                            key={`${index}-${image.slice(-12)}`}
                                            type="button"
                                            onClick={() => setImages((current) => current.filter((_, i) => i !== index))}
                                            className={cx('rounded-md border p-0.5', color.auto.borderSubtle)}
                                            aria-label={t('support.removeImage')}
                                        >
                                            <img src={image} alt="" className="h-10 w-10 rounded object-cover" />
                                        </button>
                                    ))}
                                </div>

                                <div className={cx('mt-4 rounded-md border p-3', color.auto.borderSubtle)}>
                                    <button
                                        type="button"
                                        onClick={() => setShowDetails((current) => !current)}
                                        className={cx('text-xs underline', color.auto.textMuted)}
                                    >
                                        {t('support.attachedNotice')} · {showDetails ? t('support.hideDetails') : t('support.showDetails')}
                                    </button>
                                    {showDetails && (
                                        <pre className={cx('mt-2 max-h-32 overflow-auto whitespace-pre-wrap break-words text-[11px]', color.auto.textFaint)}>
                                            {attachments.device}
                                            {'\n'}
                                            {attachments.context}
                                        </pre>
                                    )}
                                </div>

                                {error && <p className={cx('mt-3 text-sm', color.auto.error)}>{error}</p>}

                                <div className="mt-5 flex justify-end gap-3">
                                    <button
                                        type="button"
                                        onClick={close}
                                        className={cx('rounded-md px-4 py-2 text-sm', color.auto.textMuted)}
                                    >
                                        {t('support.cancel')}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => void submit()}
                                        disabled={status === 'sending' || message.trim().length < 3 || !emailValid}
                                        className={cx(
                                            'rounded-md px-4 py-2 text-sm text-white transition disabled:opacity-40',
                                            color.auto.accentStrong,
                                            color.auto.accentStrongHover,
                                        )}
                                    >
                                        {status === 'sending' ? t('support.sending') : t('support.send')}
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}
        </>
    );
};

export default SupportFeedbackWidget;
